import type { MouseEventHandler, ReactNode } from 'react';
import { Icon } from '../../atoms/Icon/Icon';
import { Button } from '../../atoms/Button/Button';
import styles from './Tag.module.css';

/**
 * Figma: `Tag` (825:21171) — a 24px pill carrying a short label, optionally with
 * an X that removes it. Two variant properties, `Type` and `State`, and no
 * annotations on the set.
 *
 * The 2026-09-20 update took the label from Medium to **Regular** across all
 * four variants and changed nothing else. That is a wider change than it looks:
 * the tag is the only place a 12px label sat at Medium beside `Text sm` body
 * copy, and dropping it to Regular is what lets a tag sit in a sentence of
 * placeholder text without out-weighing it.
 *
 * Neither becomes an enum prop:
 *
 * - `Type=X close | Text only` is decided by whether the tag can be removed, so
 *   it follows from `onRemove` rather than being stated twice.
 * - `State=Default | Hover` is the surface going `surface/neutral-subtle` →
 *   `surface/card`. That is CSS, not API — `state` exists only to force it for
 *   documentation and visual regression.
 *
 * The X is not a bespoke control: Figma nests a real `Buttons/Button` instance
 * with `Hierarchy=Ghost Grey, Icon only=True`, which is exactly
 * `<Button hierarchy="ghostGrey">` at 20x20, so this composes it.
 */

/** Force a state for documentation and visual regression only. */
export type TagState = 'default' | 'hover';

export type TagProps = {
  /** Figma's `Text` layer — the label. */
  children: ReactNode;
  /**
   * Figma `Type=X close`. Supplying a handler renders the X; omitting it gives
   * `Type=Text only`.
   */
  onRemove?: MouseEventHandler<HTMLElement>;
  /**
   * Accessible name for the X. The button's only content is an icon, so it has
   * to be named; the tag's own label does not reach it. Not in Figma — Figma has
   * no concept of an accessible name.
   */
  removeLabel?: string;
  state?: TagState;
  className?: string;
};

export function Tag({
  children,
  onRemove,
  removeLabel = 'Remove',
  state,
  className,
}: TagProps) {
  const removable = onRemove !== undefined;

  const classes = [
    styles.tag,
    removable ? styles.withClose : styles.textOnly,
    state === 'hover' ? styles.hover : null,
    /*
     * Hover is live only when no state is forced, so `:hover` reacts in the app
     * and stays inert in a states story.
     */
    state === undefined ? styles.interactive : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <span className={classes}>
      <span className={styles.content}>
        <span className={styles.label}>{children}</span>
      </span>
      {removable && (
        <Button
          className={styles.close}
          hierarchy="ghostGrey"
          iconLeading={<Icon name="close" />}
          onClick={onRemove}
          aria-label={removeLabel}
        />
      )}
    </span>
  );
}
